import { AppLayout } from "@/components/layout/AppLayout";
import { useDecisionMetrics, Alert } from "@/hooks/useDecisionMetrics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, TrendingDown, Gauge, ChevronDown, ChevronUp, Check, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

const iconMap = {
  underperforming: TrendingDown,
  expense_spike: AlertTriangle,
  low_utilization: Gauge,
};

const AlertCard = ({ alert }: { alert: Alert }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState<"none" | "investigating" | "ignored">("none");
  const Icon = iconMap[alert.type];

  if (status === "ignored") return null;

  return (
    <Card className={status === "investigating" ? "border-primary/40" : ""}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-destructive/10 p-2.5 mt-0.5">
            <Icon className="h-5 w-5 text-destructive" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-foreground leading-tight">{alert.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{alert.reason}</p>
              </div>
              <Badge
                variant={alert.confidence === "high" ? "default" : "secondary"}
                className="shrink-0"
              >
                {alert.confidence} confidence
              </Badge>
            </div>

            <p className="text-xs text-muted-foreground mt-2">
              Compared to: {alert.comparedTo}
            </p>

            {status === "investigating" && (
              <Badge variant="outline" className="mt-2 text-primary border-primary/30">
                Investigating
              </Badge>
            )}

            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-8"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
                {expanded ? "Less" : "Details"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-8"
                onClick={() => setStatus(status === "investigating" ? "none" : "investigating")}
              >
                <Check className="h-3.5 w-3.5 mr-1" />
                {status === "investigating" ? "Clear" : "Investigating"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-8 text-muted-foreground"
                onClick={() => setStatus("ignored")}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Ignore for now
              </Button>
            </div>

            {expanded && (
              <div className="mt-4 space-y-3 border-t pt-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Why this matters</p>
                  <p className="text-sm text-foreground">
                    {alert.type === "underperforming" && "This vehicle is generating less profit than others in your fleet. Understanding why can help you decide whether to adjust pricing, reduce costs, or reconsider the vehicle."}
                    {alert.type === "expense_spike" && "A sudden increase in expenses could signal a one-time event or an emerging pattern. Investigating now prevents it from silently eroding your margins."}
                    {alert.type === "low_utilization" && "Low utilization means this vehicle is sitting idle more often than similar vehicles. This could indicate a pricing issue, listing visibility problem, or seasonal pattern."}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Suggested actions</p>
                  <ul className="text-sm text-foreground space-y-1">
                    {alert.type === "underperforming" && (
                      <>
                        <li>• Review this vehicle's expense breakdown</li>
                        <li>• Compare pricing with better-performing vehicles</li>
                        <li>• Check if recent trips had unusually low earnings</li>
                      </>
                    )}
                    {alert.type === "expense_spike" && (
                      <>
                        <li>• Check for large one-time expenses (repair, insurance)</li>
                        <li>• Compare with the prior month's line items</li>
                        <li>• Determine if the increase is recurring or one-off</li>
                      </>
                    )}
                    {alert.type === "low_utilization" && (
                      <>
                        <li>• Consider adjusting daily pricing or discounts</li>
                        <li>• Review listing photos and description quality</li>
                        <li>• Check if the pickup location is convenient</li>
                      </>
                    )}
                  </ul>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/app/vehicles">View vehicle details</Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/app/expenses">View expenses</Link>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const UnderperformingVehicles = () => {
  const { alerts, isLoading, vehicleData } = useDecisionMetrics();

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Underperforming Vehicles</h1>
          <p className="text-muted-foreground mt-1">
            Alerts framed as decisions — each one tells you what's happening, why it matters, and what you might do.
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : vehicleData.length < 2 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold text-foreground">Need more data</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                Alerts require at least two vehicles with trip data so comparisons can be made. Add vehicles and import earnings to get started.
              </p>
              <div className="flex justify-center gap-3 mt-4">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/app/vehicles">Add vehicles</Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/app/earnings">Import earnings</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : alerts.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Check className="h-10 w-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground">All vehicles performing within range</h3>
              <p className="text-sm text-muted-foreground mt-1">
                No alerts right now. HostCaptain is watching for underperformance, expense spikes, and low utilization.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <AlertCard key={alert.id} alert={alert} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default UnderperformingVehicles;
